import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight } from 'lucide-react';
import { fetchPerspectiveGroups } from '../../apis/perspectives.api';
import EventCard from './EventCard';
import PerspectivesExplainerModal from '../../routes/PerspectivesViewer/components/PerspectivesExplainerModal';

const PerspectivesTabContent = () => {
  const [explainerModalOpen, setExplainerModalOpen] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['perspectiveGroups'],
    queryFn: () => fetchPerspectiveGroups({ limit: 20 }),
    staleTime: 60_000
  });

  const groups = data?.data || [];

  if (isLoading) return <LoadingSkeleton />;

  if (error) {
    return (
      <div className="py-16 text-center">
        <p className="text-gray-400">Failed to load perspectives. Please try again.</p>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="py-16 text-center">
        <p className="text-lg text-gray-500 font-semibold mb-2">No perspectives yet</p>
        <p className="text-sm text-gray-400">
          Perspective articles will appear here once published.
        </p>
      </div>
    );
  }

  const featured = groups[0];
  const rest = groups.slice(1);

  return (
    <div className="px-4 md:px-6 pt-6 pb-16">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2.5 mb-1.5">
          <h2
            className="text-[22px] font-extrabold text-slate-900"
            style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
          >
            Today's Perspectives
          </h2>
          <span
            className="text-[11px] font-bold px-2 py-0.5 rounded-md text-green-600"
            style={{
              background: 'rgba(5,150,105,0.08)',
              fontFamily: "'JetBrains Mono', monospace"
            }}
          >
            {groups.length} active
          </span>
        </div>
        <p className="text-sm text-slate-400">
          Recent events and stories explored from multiple angles, with ongoing discussion.
        </p>
      </div>

      {/* AI disclosure bar */}
      <div
        className="flex items-center gap-2 px-3.5 py-2.5 rounded-xl mb-6"
        style={{ background: 'rgba(15,23,42,0.025)' }}
      >
        <span
          className="shrink-0 text-[9px] font-extrabold tracking-wide px-1.5 py-0.5 rounded"
          style={{
            background: '#0f172a',
            color: '#a5f3fc',
            fontFamily: "'JetBrains Mono', monospace"
          }}
        >
          AI
        </span>
        <span className="text-xs text-slate-500">
          Each topic starts with AI perspectives that synthesize what people are saying across news,
          social media, and public debate. Community members add what the synthesis misses — the
          best get featured.{' '}
          <button
            type="button"
            onClick={() => setExplainerModalOpen(true)}
            className="text-green-600 font-semibold cursor-pointer hover:underline bg-transparent border-none p-0 inline"
          >
            How it works
          </button>
        </span>
      </div>

      <PerspectivesExplainerModal
        isOpen={explainerModalOpen}
        onClose={() => setExplainerModalOpen(false)}
      />

      {/* Featured event */}
      <div className="mb-5">
        <EventCard event={featured} featured />
      </div>

      {/* Rest in 2-col grid */}
      {rest.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {rest.map((event) => (
            <EventCard key={event.id || event.slug} event={event} />
          ))}
        </div>
      )}

      {/* Archive link */}
      <div className="mt-8 p-5 rounded-2xl border border-dashed border-stone-300 text-center">
        <p className="text-sm text-slate-400 mb-2">Looking for previous topics?</p>
        <span className="text-sm text-green-600 font-semibold cursor-pointer inline-flex items-center gap-1 hover:underline">
          Browse archive <ArrowRight size={14} />
        </span>
      </div>
    </div>
  );
};

function LoadingSkeleton() {
  return (
    <div className="px-4 md:px-6 pt-6 pb-16 animate-pulse">
      <div className="mb-6">
        <div className="h-7 w-56 bg-gray-200 rounded mb-2" />
        <div className="h-4 w-96 bg-gray-100 rounded" />
      </div>
      <div className="h-10 w-full bg-gray-100 rounded-xl mb-6" />
      <div className="h-72 w-full bg-gray-200 rounded-2xl mb-5" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="h-56 bg-gray-200 rounded-2xl" />
        <div className="h-56 bg-gray-200 rounded-2xl" />
      </div>
    </div>
  );
}

export default PerspectivesTabContent;
